import React, { Fragment, useState, useEffect } from 'react';
import { Formik } from 'formik';
import { Subtext, BlankLink } from '@contently/toolkit';
import { isEmpty } from 'underscore';
import { get, endpoint } from '../../utils/api';
import { handleSubmitError } from '../../utils/errors';
import GrapeFormat from '../../utils/formatters/formik-grape-formatter';
import DynamicFormikForm from '../forms/dynamic_forms/DynamicFormikForm';
import PropertiesAndTagsCheckboxes from './PropertiesAndTagsCheckboxes';
import ContentRequestFormSelect from '../forms/ContentRequestFormSelect';

const config = {
  cancelLinkLocation: renderProps => `/organizations/${renderProps.accountId}/integrations`,
  fields: [
    {
      label: 'Name',
      id: 'name',
      type: 'text',
      required: true,
      subtext: 'This name will be used to distinguish your integrations from each other.'
    },
    {
      label: 'Link a Content Request Form',
      id: 'contentCollectionAttributes.contentRequestFormId',
      customRender: renderProps => (
        <ContentRequestFormSelect
          renderSubtext={() => {
            return (
              <Fragment>
                <Subtext>
                  You can include a link to one of your existing content request forms.&nbsp;
                  <BlankLink
                    href="https://help.contently.com/planning-your-content/ideation-pitches-and-content-requests/content-requests"
                    title="Content request form help"
                  >
                    Learn more
                  </BlankLink>
                </Subtext>
              </Fragment>
            );
          }}
          {...renderProps}
        />
      ),
      needsFetch: true
    },
    {
      id: 'contentCollectionAttributes.propertyAndTagIds',
      customRender: renderProps => <PropertiesAndTagsCheckboxes {...renderProps} />,
      needsFetch: true,
      required: true
    }
  ]
};

function ContentPortalForm(props) {
  const { accountId, platform, method, methods } = props;
  const [initialValues, setInitialValues] = useState({});
  const [record, setRecord] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function fetchIntegration() {
    return get(
      endpoint(`/api/v1/integrations/${methods[method].getPath}`, { accountId, platform })
    ).then(data => {
      data.contentCollection = data.contentCollection || {};
      setRecord(data);
      setInitialValues({
        id: data.id,
        name: data.name,
        url: data.url,
        apiKey: data.apiKey,
        connected: data.connected,
        platform: data.platform,
        emailAddresses: data.emailAddresses,
        accountId: data.accountId,
        externalApiKey: data.externalApiKey,
        brandProfileIds: data.brandProfileIds,
        contentCollectionAttributes: {
          id: data.contentCollection.id,
          accountId: data.accountId,
          contentRequestFormId: data.contentCollection.contentRequestFormId,
          propertyIds: data.contentCollection.propertyIds || [],
          tagIds: data.contentCollection.tagIds || [],
          enabledTagGroupIds: data.contentCollection.enabledTagGroupIds || []
        }
      });
    });
  }

  useEffect(() => {
    fetchIntegration();
  }, []);

  if (isEmpty(initialValues)) return <Fragment />;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, actions) => {
        setIsSubmitting(true);
        methods[method]
          .apiFunc(
            `/api/v1/integrations/${methods[method].savePath}`,
            JSON.stringify(GrapeFormat(values))
          )
          .then(response => {
            setIsSubmitting(false);
            if (record.isPersisted) {
              window.location.replace(`/organizations/${accountId}/portals`);
            } else {
              const integration = response.data.attributes;
              window.location.replace(
                `/organizations/${accountId}/integrations/instructions?api_key=${
                  integration.apiKey
                }&platform=${integration.platform}`
              );
            }
          })
          .catch(errorResponse => {
            setIsSubmitting(false);
            handleSubmitError(
              errorResponse,
              actions,
              'There was an error saving your content hub.'
            );
          });
      }}
      render={innerProps => {
        return (
          <DynamicFormikForm
            {...innerProps}
            accountId={accountId}
            record={record}
            isSubmitting={isSubmitting}
            config={config}
          />
        );
      }}
    />
  );
}

export default ContentPortalForm;



// WEBPACK FOOTER //
// ./app/javascript/app/components/integrations/ContentPortalForm.jsx